import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useCreateArticleBrief, useUpdateArticleBrief } from "../features/article-brief/api/articleBriefQueries";
import type { ArticleBrief } from "../types/article-brief";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  X,
  Plus,
  ArrowRight,
  ArrowLeft,
  Loader2,
  CheckCircle,
  XCircle,
  RefreshCw,
  Download,
  ChevronDown,
} from "lucide-react";
import { useDownloadQueue } from "../hooks/useDownloadQueue";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import { cn } from "../lib/utils";
import {
  Select,
  SelectContent,
  SelectItemWithDescription,
  SelectTrigger,
  SelectValue
} from './ui/select';
import {
  CONTENT_TYPES,
  CONTENT_LENGTHS,
  type ContentTypeValue,
  type ContentLengthValue
} from '../constants/deepResearch';
import { API_URL } from '../config';
import type { OutlineGuide } from '../types/outline-guide';

// Form validation schema (matches backend validation)
const researchDefinitionSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(200, "Name must be 200 characters or less"),
  title: z.string().trim().min(1, "Title is required").max(500, "Title must be 500 characters or less"),
  task: z.string().trim().min(1, "Task is required").max(5000, "Task must be 5000 characters or less"),
  description: z.string().trim().max(5000, "Description must be 5000 characters or less").optional(),
  urls: z
    .array(z.string().url("Must be a valid URL").max(2000, "URL must be 2000 characters or less"))
    .min(1, "At least one URL is required")
    .max(100, "Maximum 100 URLs allowed"),
});

type ResearchDefinitionFormData = z.infer<typeof researchDefinitionSchema>;

interface ResearchDefinitionFormProps {
  initialData?: ArticleBrief;
  mode?: "create" | "edit";
  onNext?: (data: ArticleBrief) => void;
  onBack?: () => void;
  showBackButton?: boolean;
}

interface UrlDownloadStatus {
  url: string;
  status: "pending" | "downloading" | "success" | "failed" | "cached";
  error?: string;
  contentLength?: number;
}

export const ArticleBriefForm: React.FC<ResearchDefinitionFormProps> = ({
  initialData,
  mode = "create",
  onNext,
  onBack,
  showBackButton = false,
}) => {
  // React Query mutations
  const createBrief = useCreateArticleBrief();
  const updateBrief = useUpdateArticleBrief();

  const [newUrl, setNewUrl] = useState("");
  const [urlStatuses, setUrlStatuses] = useState<
    Map<string, UrlDownloadStatus>
  >(new Map());
  const [isDownloadingUrls, setIsDownloadingUrls] = useState(false);
  const [downloadPhase, setDownloadPhase] = useState<
    "editing" | "downloading" | "complete"
  >("editing");
  const [isReferencesOpen, setIsReferencesOpen] = useState(false);
  const [contentType, setContentType] = useState<string>('linkedin-article');
  const [contentLength, setContentLength] = useState<ContentLengthValue>('medium');
  const [outlineGuides, setOutlineGuides] = useState<OutlineGuide[]>([]);
  const [isLoadingGuides, setIsLoadingGuides] = useState(false);

  // Track active download promises to avoid polling
  const activeDownloads = React.useRef<Map<string, Promise<UrlDownloadStatus>>>(
    new Map()
  );

  // Download lock to prevent race conditions between background downloads and form submission
  const downloadLockRef = React.useRef<boolean>(false);

  // Promise queue for lock management (replaces busy-wait pattern)
  const downloadLockQueue = React.useRef<Array<() => void>>([]);

  // Initialize download queue hook
  const downloadQueue = useDownloadQueue<string, UrlDownloadStatus>();

  // Track original URLs to detect changes (for smart downloading in edit mode)
  // Use useRef to capture initial value only once, never updates
  const originalUrlsRef = React.useRef<string[]>(initialData?.urls || []);
  const originalUrls = originalUrlsRef.current;

  // Prevent duplicate URL status checks from React StrictMode
  const hasCheckedUrlStatuses = React.useRef(false);

  React.useEffect(() => {
    const fetchGuides = async () => {
      setIsLoadingGuides(true);
      try {
        const response = await fetch(`${API_URL}/api/outline-guides`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.outlineGuides) {
            setOutlineGuides(data.outlineGuides.filter((g: OutlineGuide) => g.isActive));
          }
        }
      } catch (error) {
        console.error('Failed to fetch outline templates:', error);
      } finally {
        setIsLoadingGuides(false);
      }
    };
    fetchGuides();
  }, []);

  const defaultValues: ResearchDefinitionFormData = initialData
    ? {
        name: initialData.name,
        title: initialData.title,
        task: initialData.task,
        description: initialData.description || "",
        urls: initialData.urls || [],
      }
    : {
        name: "",
        title: "",
        task: "",
        description: "",
        urls: [],
      };

  const {
    register,
    watch,
    setValue,
    getValues,
    setFocus,
    setError,
    formState: { errors },
  } = useForm<ResearchDefinitionFormData>({
    resolver: zodResolver(researchDefinitionSchema),
    defaultValues,
    mode: 'onBlur', // Validate when field loses focus
    reValidateMode: 'onChange', // Then validate on every change after first validation
  });

  const watchedUrls = watch("urls");

  // Initialize content settings from metadata when editing
  React.useEffect(() => {
    if (!initialData?.metadata) return;

    const meta = initialData.metadata as Record<string, unknown>;

    // Validate contentType against known values
    if (typeof meta.contentType === 'string') {
      const validTypes = CONTENT_TYPES.map(t => t.value);
      if (validTypes.includes(meta.contentType)) {
        setContentType(meta.contentType as ContentTypeValue);
      }
    }

    // Validate contentLength against known values
    if (typeof meta.contentLength === 'string') {
      const validLengths = CONTENT_LENGTHS.map(l => l.value);
      if (validLengths.includes(meta.contentLength)) {
        setContentLength(meta.contentLength as ContentLengthValue);
      }
    }
  }, [initialData]);

  // Check download status of original URLs when component mounts in edit mode
  React.useEffect(() => {
    const checkUrlStatuses = async () => {
      // Prevent duplicate checks from React StrictMode
      if (
        hasCheckedUrlStatuses.current ||
        mode !== "edit" ||
        originalUrls.length === 0
      ) {
        return;
      }

      hasCheckedUrlStatuses.current = true;
      const statuses = new Map<string, UrlDownloadStatus>();

      // Check each original URL's download status
      for (const url of originalUrls) {
        try {
          const response = await fetch("/api/download", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ url, forceDownload: false }),
          });

          const result = await response.json();

          if (response.ok && result.fromCache) {
            // URL is already downloaded/cached
            statuses.set(url, {
              url,
              status: "cached",
              contentLength: result.contentLength,
            });
          } else {
            // URL not downloaded yet - will need to download
            statuses.set(url, {
              url,
              status: "pending",
            });
          }
        } catch (error) {
          // Error checking status - assume needs download
          console.error(`Error checking status for ${url}:`, error);
          statuses.set(url, {
            url,
            status: "pending",
          });
        }
      }

      setUrlStatuses(statuses);
    };

    checkUrlStatuses().catch((error) => {
      console.error("Error in checkUrlStatuses:", error);
    });
  }, [mode, originalUrls]);

  // Lock management functions (promise-based queue, not busy-wait)
  const acquireLock = async (): Promise<void> => {
    if (!downloadLockRef.current) {
      downloadLockRef.current = true;
      return;
    }

    // Wait for lock release via promise queue
    await new Promise<void>((resolve) => {
      downloadLockQueue.current.push(resolve);
    });
  };

  const releaseLock = (): void => {
    const next = downloadLockQueue.current.shift();
    if (next) {
      // Pass lock to next waiting caller
      next();
    } else {
      // No waiters, release lock
      downloadLockRef.current = false;
    }
  };

  const addUrl = () => {
    const trimmedUrl = newUrl.trim();
    if (trimmedUrl && !watchedUrls.includes(trimmedUrl)) {
      setValue("urls", [...watchedUrls, trimmedUrl]);
      setNewUrl("");

      // ✨ Start download immediately in background with error handling
      startDownloadInBackground(trimmedUrl).catch((error) => {
        console.error(
          `Error starting background download for ${trimmedUrl}:`,
          error
        );
      });
    }
  };

  // Start a single URL download in background
  const startDownloadInBackground = async (url: string) => {
    // Acquire lock using promise-based queue (no busy-wait)
    await acquireLock();

    try {
      // Check if already downloading
      if (activeDownloads.current.has(url)) {
        return;
      }

      // Mark as downloading immediately
      setUrlStatuses((prev) => {
        const updated = new Map(prev);
        updated.set(url, { url, status: "downloading" });
        return updated;
      });

      // Create and track the download promise
      const downloadPromise = downloadUrl(url)
        .then((result) => {
          // Update status with result
          setUrlStatuses((prev) => {
            const updated = new Map(prev);
            updated.set(url, result);
            return updated;
          });

          // Remove from active downloads
          activeDownloads.current.delete(url);

          return result;
        })
        .catch((error) => {
          // Handle download errors
          const failedStatus: UrlDownloadStatus = {
            url,
            status: "failed",
            error: error instanceof Error ? error.message : "Unknown error",
          };

          setUrlStatuses((prev) => {
            const updated = new Map(prev);
            updated.set(url, failedStatus);
            return updated;
          });

          // Remove from active downloads
          activeDownloads.current.delete(url);

          return failedStatus;
        });

      activeDownloads.current.set(url, downloadPromise);

      return downloadPromise;
    } finally {
      // Always release lock when done
      releaseLock();
    }
  };

  const removeUrl = (index: number) => {
    const newUrls = [...watchedUrls];
    newUrls.splice(index, 1);
    setValue("urls", newUrls);
  };

  const downloadUrl = async (url: string): Promise<UrlDownloadStatus> => {
    try {
      const response = await fetch("/api/download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url, forceDownload: false }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.details || result.error || "Download failed");
      }

      return {
        url,
        status: result.fromCache ? "cached" : "success",
        contentLength: result.contentLength,
      };
    } catch (error) {
      return {
        url,
        status: "failed",
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  };

  const retryDownload = async (url: string) => {
    const statuses = new Map(urlStatuses);

    // Update to downloading
    statuses.set(url, { url, status: "downloading" });
    setUrlStatuses(new Map(statuses));

    // Perform download
    const result = await downloadUrl(url);
    statuses.set(url, result);
    setUrlStatuses(new Map(statuses));
  };

  const retryAllFailed = async () => {
    const failedUrls = Array.from(urlStatuses.entries())
      .filter(([_, status]) => status.status === "failed")
      .map(([url]) => url);

    for (const url of failedUrls) {
      await retryDownload(url);
    }
  };

  // Helper to handle retry with error handling (extracted to reduce nesting depth)
  const handleRetry = async (url: string) => {
    try {
      await retryDownload(url);
    } catch (error) {
      console.error(`Error retrying download for ${url}:`, error);
    }
  };

  // Helper to validate form data and show errors (extracted to reduce cognitive complexity)
  const validateFormData = (data: ResearchDefinitionFormData): ResearchDefinitionFormData | null => {
    const validationResult = researchDefinitionSchema.safeParse(data);

    if (!validationResult.success) {
      const firstError = validationResult.error.issues[0];

      // Set field error and focus for better UX
      if (firstError?.path && firstError.path.length > 0) {
        const fieldName = firstError.path[0] as keyof ResearchDefinitionFormData;
        setError(fieldName, {
          type: "manual",
          message: firstError.message
        });
        setFocus(fieldName);
      }

      toast.error("Validation Error", {
        description: firstError?.message || "Please fill in all required fields"
      });
      return null;
    }

    return validationResult.data;
  };

  // Helper: Get submit button content based on state (extracted to avoid nested ternary)
  const getSubmitButtonContent = () => {
    if (isDownloadingUrls) {
      const downloadMessage = downloadQueue.stats.totalCount > 0
        ? `Downloading Sources (${downloadQueue.stats.completedCount + downloadQueue.stats.failedCount}/${downloadQueue.stats.totalCount})`
        : "Downloading Sources...";

      return (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          {downloadMessage}
        </>
      );
    }

    if (createBrief.isPending || updateBrief.isPending) {
      return (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Saving...
        </>
      );
    }

    if (downloadPhase === "complete") {
      return (
        <>
          Continue
          <ArrowRight className="h-4 w-4 ml-2" />
        </>
      );
    }

    return (
      <>
        Next
        <ArrowRight className="h-4 w-4 ml-2" />
      </>
    );
  };

  // Helper to filter out failed URLs and show notifications (extracted to reduce cognitive complexity)
  const filterFailedUrls = (briefData: { urls: string[] }): boolean => {
    const failedUrls = Array.from(urlStatuses.entries())
      .filter(([_, status]) => status.status === 'failed')
      .map(([url]) => url);

    // Remove failed URLs from submission
    if (failedUrls.length > 0) {
      briefData.urls = briefData.urls.filter(
        (url: string) => !failedUrls.includes(url)
      );

      // Check if all URLs failed
      if (briefData.urls.length === 0) {
        toast.error("All URLs Failed", {
          description: "All URLs failed to download. Please add different URLs."
        });
        return false;
      }

      // Show notification about removed URLs
      toast.warning("Failed URLs Removed", {
        description: `${failedUrls.length} URL(s) failed to download and were removed from the brief.`
      });
    }

    return true;
  };

  const getDownloadSummary = () => {
    const statusArray = Array.from(urlStatuses.values());
    const successful = statusArray.filter(
      (s) => s.status === "success" || s.status === "cached"
    ).length;
    const failed = statusArray.filter((s) => s.status === "failed").length;
    const inProgress = statusArray.filter(
      (s) => s.status === "downloading"
    ).length;
    const pending = statusArray.filter((s) => s.status === "pending").length;

    return {
      successful,
      failed,
      inProgress,
      pending,
      total: statusArray.length,
    };
  };

  const handleNext = async (data: ResearchDefinitionFormData) => {
    // Manual validation guard - ensures invalid data never reaches API
    const validatedData = validateFormData(data);
    if (!validatedData) return;

    try {
      // Add metadata with content settings
      const briefData = {
        ...validatedData,
        metadata: {
          ...(initialData?.metadata as Record<string, unknown> || {}),
          contentType,
          contentLength
        }
      };

      // Check for failed downloads and remove them
      if (!filterFailedUrls(briefData)) {
        return;
      }

      // Use update for edit mode, create for new definitions
      let savedBrief: ArticleBrief;

      if (mode === "edit" && initialData) {
        savedBrief = await updateBrief.mutateAsync({
          briefId: initialData.id,
          data: briefData,
        });
      } else {
        savedBrief = await createBrief.mutateAsync(briefData);
      }

      // ✨ Wait for any in-progress downloads to complete
      if (briefData.urls && briefData.urls.length > 0) {
        await waitForDownloadsToComplete(briefData.urls);
      }

      // Auto-navigate after downloads complete - pass the saved brief
      onNext?.(savedBrief);
    } catch (error) {
      console.error("Failed to save research definition:", error);

      // Show user-friendly error message
      const errorMessage = error instanceof Error ? error.message : "Failed to save article brief";
      toast.error("Save Failed", {
        description: errorMessage
      });
    }
  };

  // Wait for all URLs to finish downloading using the queue (max 5 concurrent, 100ms delay)
  const waitForDownloadsToComplete = async (urls: string[]) => {
    // Acquire lock via the shared queue to prevent new downloads from starting
    await acquireLock();

    try {
      // Collect all active download promises for these URLs
      const activePromises: Promise<UrlDownloadStatus>[] = [];
      const needsDownload: string[] = [];

      for (const url of urls) {
        const activePromise = activeDownloads.current.get(url);

        if (activePromise) {
          activePromises.push(activePromise);
          continue;
        }

        const status = urlStatuses.get(url);
        const isComplete = status && ['success', 'cached', 'failed'].includes(status.status);

        if (!isComplete) {
          needsDownload.push(url);
        }
      }

      if (activePromises.length > 0 || needsDownload.length > 0) {
        setDownloadPhase("downloading");
        setIsDownloadingUrls(true);

        // Wait for active downloads
        if (activePromises.length > 0) {
          await Promise.allSettled(activePromises);
        }

        // Keep the lock held for the duration of this function so
        // startDownloadInBackground calls will wait until we're done.

        // Start any new downloads using the queue (max 5 concurrent, 100ms delay)
        if (needsDownload.length > 0) {

          // Update status to show queued state
          needsDownload.forEach((url) => {
            setUrlStatuses((prev) => {
              const updated = new Map(prev);
              updated.set(url, { url, status: "pending" });
              return updated;
            });
          });

          // Process downloads through queue
          await downloadQueue.processUrls(
            needsDownload,
            async (url) => {
              // Update status to downloading
              setUrlStatuses((prev) => {
                const updated = new Map(prev);
                updated.set(url, { url, status: "downloading" });
                return updated;
              });

              const result = await downloadUrl(url);

              // Update final status
              setUrlStatuses((prev) => {
                const updated = new Map(prev);
                updated.set(url, result);
                return updated;
              });

              return result;
            },
            (url) => url
          );
        }

        setIsDownloadingUrls(false);
        setDownloadPhase("complete");
      } else {
        setDownloadPhase("complete");
      }
    } catch (error) {
      console.error("Error in waitForDownloadsToComplete:", error);
      setIsDownloadingUrls(false);
      setDownloadPhase("complete");
    } finally {
      // Always release lock when done (using releaseLock to handle queue)
      releaseLock();
    }
  };

  const summary = getDownloadSummary();
  const hasFailedDownloads = summary.failed > 0;

  return (
    <div className="space-y-6">
      <form onSubmit={(e) => {
        e.preventDefault();
        void handleNext(getValues());
      }} className="space-y-8">
        {/* Form Actions - Top */}
        <div className="flex justify-between items-center">
          {showBackButton && onBack ? (
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              disabled={isDownloadingUrls}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          ) : (
            <div />
          )}
          <div className="flex space-x-2">
            {downloadPhase === "complete" && hasFailedDownloads && (
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  retryAllFailed().catch((error) => {
                    console.error("Error retrying all failed downloads:", error);
                  })
                }
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry Failed ({summary.failed})
              </Button>
            )}
            <Button
              type="button"
              onClick={() => void handleNext(getValues())}
              disabled={
                isDownloadingUrls ||
                createBrief.isPending ||
                updateBrief.isPending
              }
            >
              {getSubmitButtonContent()}
            </Button>
          </div>
        </div>

        {/* Basic Information */}
        <Card className="border-ochre/10 bg-paper-warm/20">
          <CardHeader className="border-b border-ochre/10 bg-paper-warm/30">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-px w-6 bg-ochre" />
              <div className="w-1 h-1 rounded-full bg-burnt-sienna" />
            </div>
            <CardTitle
              className="text-xl text-charcoal"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Article Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            {mode === "create" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-charcoal font-medium">
                    Project Name <span className="text-burnt-sienna">*</span>
                  </Label>
                  <Input
                    id="name"
                    {...register("name")}
                    placeholder="my-ai-ethics-article"
                    className={cn(
                      "bg-paper-light/50 border-ochre/20 focus:border-ochre focus:ring-ochre/20",
                      errors.name && "border-destructive animate-shake"
                    )}
                    disabled={downloadPhase === "downloading"}
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive mt-1 font-semibold">
                      {errors.name.message}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="title" className="text-charcoal font-medium">
                Title <span className="text-burnt-sienna">*</span>
              </Label>
              <Input
                id="title"
                {...register("title")}
                placeholder="The Ethics of Artificial Intelligence"
                className={cn(
                  "bg-paper-light/50 border-ochre/20 focus:border-ochre focus:ring-ochre/20",
                  errors.title && "border-destructive animate-shake"
                )}
                disabled={downloadPhase === "downloading"}
              />
              {errors.title && (
                <p className="text-sm text-destructive mt-1 font-semibold">
                  {errors.title.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="task" className="text-charcoal font-medium">
                What should this article cover? <span className="text-burnt-sienna">*</span>
              </Label>
              <Textarea
                id="task"
                {...register("task")}
                placeholder="Research and analyze the ethical implications..."
                rows={3}
                className={cn(
                  "bg-paper-light/50 border-ochre/20 focus:border-ochre focus:ring-ochre/20",
                  errors.task && "border-destructive animate-shake"
                )}
                disabled={downloadPhase === "downloading"}
              />
              {errors.task && (
                <p className="text-sm text-destructive mt-1 font-semibold">
                  {errors.task.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="description" className="text-charcoal font-medium">
                Additional notes <span className="text-muted-warm">(optional)</span>
              </Label>
              <Textarea
                id="description"
                {...register("description")}
                placeholder="Any extra details we should know..."
                rows={4}
                className={cn(
                  "bg-paper-light/50 border-ochre/20 focus:border-ochre focus:ring-ochre/20",
                  errors.description && "border-destructive animate-shake"
                )}
                disabled={downloadPhase === "downloading"}
              />
              {errors.description && (
                <p className="text-sm text-destructive mt-1 font-semibold">
                  {errors.description.message}
                </p>
              )}
            </div>

            {/* Content Configuration - only shown in create mode */}
            {mode === "create" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-ochre/10">
                <div className="space-y-2">
                  <Label htmlFor="contentType" className="text-charcoal font-medium">
                    Outline Template
                  </Label>
                    <Select
                      value={contentType}
                      onValueChange={(value) => setContentType(value)}
                      disabled={downloadPhase === "downloading" || isLoadingGuides}
                    >
                      <SelectTrigger
                        id="contentType"
                        className="bg-paper-light/50 border-ochre/20 focus:border-ochre focus:ring-ochre/20"
                      >
                        <SelectValue placeholder={isLoadingGuides ? "Loading templates..." : "Select outline template"} />
                      </SelectTrigger>
                      <SelectContent>
                        {outlineGuides.length > 0 ? (
                          outlineGuides.map((guide) => (
                            <SelectItemWithDescription
                              key={guide.id}
                              value={guide.contentType}
                              label={guide.name}
                              description={`Template for ${guide.contentType}`}
                            />
                          ))
                        ) : (
                          CONTENT_TYPES.map((type) => (
                            <SelectItemWithDescription
                              key={type.value}
                              value={type.value}
                              label={type.label}
                              description={type.description}
                            />
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  <p className="text-sm text-muted-warm">
                    The template defines the structure and core requirements of your outline
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contentLength" className="text-charcoal font-medium">
                    Target Length
                  </Label>
                  <Select
                    value={contentLength}
                    onValueChange={(value) => setContentLength(value as ContentLengthValue)}
                    disabled={downloadPhase === "downloading"}
                  >
                    <SelectTrigger
                      id="contentLength"
                      className="bg-paper-light/50 border-ochre/20 focus:border-ochre focus:ring-ochre/20"
                    >
                      <SelectValue placeholder="Select target length" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTENT_LENGTHS.map((length) => (
                        <SelectItemWithDescription
                          key={length.value}
                          value={length.value}
                          label={length.label}
                          description={length.description}
                        />
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-warm">
                    Select the desired article length
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reference Documents */}
        <Card className="border-ochre/10 bg-paper-warm/20">
          <Collapsible
            open={isReferencesOpen}
            onOpenChange={setIsReferencesOpen}
          >
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-paper-warm/40 transition-colors border-b border-ochre/10 bg-paper-warm/30">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-px w-6 bg-ochre" />
                  <div className="w-1 h-1 rounded-full bg-burnt-sienna" />
                </div>
                <CardTitle className="flex items-center justify-between">
                  <span
                    className="text-xl text-charcoal"
                    style={{ fontFamily: 'var(--font-display)' }}
                  >
                    Your Source Material ({watchedUrls.length})
                  </span>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 text-ochre transition-transform duration-200",
                      isReferencesOpen && "rotate-180"
                    )}
                  />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>

            <CollapsibleContent className="animate-in slide-in-from-top-2 duration-200">
              <CardContent className="space-y-4 pt-6">
                {downloadPhase === "editing" && (
                  <div className="flex space-x-2">
                    <Input
                      value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                      placeholder="https://example.com"
                      className="bg-paper-light/50 border-ochre/20 focus:border-ochre focus:ring-ochre/20"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addUrl();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      onClick={addUrl}
                      disabled={!newUrl.trim()}
                      className="bg-ochre hover:bg-ochre/90 text-white"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {watchedUrls.length > 0 && (
                  <div className="space-y-2">
                    {watchedUrls.map((url, index) => {
                      const status = urlStatuses.get(url);

                      return (
                        <div
                          key={url}
                          className="flex items-center justify-between p-3 bg-paper-warm/30 border border-ochre/10 rounded-lg hover:border-ochre/30 transition-colors"
                        >
                          <div className="flex items-center space-x-2 flex-1 min-w-0 mr-2">
                            <span className="text-sm font-mono truncate text-charcoal/80">
                              {url}
                            </span>

                            {/* Status indicator */}
                            {status && (
                              <div className="flex items-center space-x-1 shrink-0">
                                {(() => {
                                  switch (status.status) {
                                    case 'pending':
                                      return (
                                        <span title="Preparing source...">
                                          <Loader2 className="h-4 w-4 text-ochre/60 animate-spin shrink-0" />
                                        </span>
                                      );
                                    case 'downloading':
                                      return (
                                        <span title="Loading source...">
                                          <Download className="h-4 w-4 text-ochre animate-pulse shrink-0" />
                                        </span>
                                      );
                                    case 'success':
                                    case 'cached':
                                      return (
                                        <span title="Source ready">
                                          <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
                                        </span>
                                      );
                                    case 'failed':
                                      return (
                                        <>
                                          <span title={status.error || "Couldn't load source"}>
                                            <XCircle className="h-4 w-4 text-burnt-sienna shrink-0" />
                                          </span>
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0 ml-1 shrink-0 hover:bg-ochre/10"
                                            onClick={() => handleRetry(url)}
                                            title="Retry loading source"
                                          >
                                            <RefreshCw className="h-3.5 w-3.5 text-ochre" />
                                          </Button>
                                        </>
                                      );
                                    default:
                                      return null;
                                  }
                                })()}
                              </div>
                            )}
                          </div>

                          {downloadPhase === "editing" && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeUrl(index)}
                              className="shrink-0 h-6 w-6 p-0 hover:bg-burnt-sienna/10 hover:text-burnt-sienna"
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {errors.urls && (
                  <p className="text-sm text-destructive">
                    {errors.urls.message}
                  </p>
                )}
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>
      </form>
    </div>
  );
};
